import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle, Info, ShieldAlert, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

export interface ChecklistItemDisplay {
  definitionId: string;
  name: string;
  description: string | null;
  isRequired: boolean;
  sortOrder: number;
  scrapedField: string | null;
  isComplete: boolean;
  completedAt: string | null;
  completedBy: { id: string; username: string } | null;
  reversedAt: string | null;
  reversedBy: { id: string; username: string } | null;
  isConfirmedOnSite: boolean;
  isSystemAction: boolean;
  autoComplete?: boolean;
  // Gate S6: non-null for conditional items; null for core items (always shown)
  evaluationTrackId?: string | null;
  // Gate S6: display name of the track; shown as a badge on conditional items
  evaluationTrackName?: string | null;
}

interface Props {
  item: ChecklistItemDisplay;
  onToggle: (definitionId: string, isComplete: boolean) => void;
  isLoading?: boolean;
  subIndicator?: React.ReactNode;
  /** Called when an admin/editor dismisses an assigned conditional item. Only rendered when provided and item is conditional. */
  onDismiss?: (definitionId: string) => void;
}

export function AdoptionListingChecklistItem({ item, onToggle, isLoading, subIndicator, onDismiss }: Props) {
  const checkboxId = `checklist-item-${item.definitionId}`;
  const isConditional = !!item.evaluationTrackId;

  // Fields sourced from the All Pets report (Chameleon) rather than the MSPCA website scrape
  const isAllPetsField = item.scrapedField === "evaluation_status" || item.scrapedField === "status";

  const infoTooltip =
    item.scrapedField && !item.isConfirmedOnSite
      ? isAllPetsField
        ? "Automatically confirmed when BETI finds this status in the All Pets report."
        : "Automatically confirmed when BETI finds this present on the MSPCA website."
      : item.scrapedField && item.isConfirmedOnSite && item.isSystemAction
        ? isAllPetsField
          ? "BETI has automatically confirmed this based on the All Pets report."
          : "BETI has automatically confirmed this is present on the MSPCA website."
        : null;

  return (
    <div
      className="flex items-start gap-3 py-2 px-2 rounded-sm"
      data-testid={`div-checklist-item-${item.definitionId}`}
    >
      <Checkbox
        id={checkboxId}
        checked={item.isComplete}
        onCheckedChange={(checked) => onToggle(item.definitionId, !!checked)}
        disabled={isLoading}
        className="mt-0.5 shrink-0"
        data-testid={`checkbox-checklist-item-${item.definitionId}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-0.5">
            <label
              htmlFor={checkboxId}
              className={cn(
                "cursor-pointer text-sm font-medium",
                item.isComplete && "text-muted-foreground line-through",
              )}
              data-testid={`label-checklist-item-${item.definitionId}`}
            >
              {item.name}
            </label>
            {subIndicator && (
              <span className="text-xs text-muted-foreground">{subIndicator}</span>
            )}
          </div>
          {infoTooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  aria-label="Checklist item info"
                  data-testid={`button-checklist-info-${item.definitionId}`}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px]">
                <p className="text-xs leading-relaxed">{infoTooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {(!item.isRequired || item.scrapedField || item.evaluationTrackName) && (
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            {!item.isRequired && (
              <Badge
                variant="outline"
                className="text-xs shrink-0"
                data-testid={`badge-checklist-optional-${item.definitionId}`}
              >
                Optional
              </Badge>
            )}
            {item.scrapedField && (
              <Badge
                variant="outline"
                className="text-xs shrink-0"
                data-testid={`badge-checklist-scraped-${item.definitionId}`}
              >
                Auto: {item.scrapedField}
              </Badge>
            )}
            {item.evaluationTrackName && (
              <Badge
                variant="outline"
                className="text-xs shrink-0"
                data-testid={`badge-checklist-track-${item.definitionId}`}
              >
                {item.evaluationTrackName}
              </Badge>
            )}
            {item.autoComplete && !item.isSystemAction && (item.completedAt || item.reversedAt) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="secondary"
                    className="text-xs shrink-0 gap-1 cursor-default"
                    data-testid={`badge-checklist-manual-override-${item.definitionId}`}
                  >
                    <ShieldAlert className="h-3 w-3" aria-hidden="true" />
                    Manual override
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[260px]">
                  <p className="text-xs leading-relaxed">
                    BETI would normally update this automatically. It will stay as-is until you interact with it again.
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        {item.isComplete && item.isSystemAction && item.completedAt && (
          <p
            className="mt-0.5 text-xs text-muted-foreground"
            data-testid={`text-checklist-attribution-${item.definitionId}`}
          >
            Completed by system on {format(new Date(item.completedAt), "MMM d, yyyy")}
          </p>
        )}
        {item.isComplete && !item.isSystemAction && item.completedBy && item.completedAt && (
          <p
            className="mt-0.5 text-xs text-muted-foreground"
            data-testid={`text-checklist-attribution-${item.definitionId}`}
          >
            Marked complete by {item.completedBy.username} on{" "}
            {format(new Date(item.completedAt), "MMM d, yyyy")}
          </p>
        )}
        {!item.isComplete && item.isSystemAction && item.reversedAt && (
          <p
            className="mt-0.5 text-xs text-muted-foreground"
            data-testid={`text-checklist-unchecked-system-${item.definitionId}`}
          >
            Unchecked by system on {format(new Date(item.reversedAt), "MMM d, yyyy")}
          </p>
        )}
        {item.isConfirmedOnSite && (
          <span
            className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"
            data-testid={`text-checklist-confirmed-${item.definitionId}`}
          >
            <CheckCircle className="h-3 w-3" aria-hidden="true" />
            {isAllPetsField ? "Confirmed via All Pets report" : "Confirmed on mspca.org"}
          </span>
        )}
      </div>
      {onDismiss && isConditional && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => onDismiss(item.definitionId)}
              disabled={isLoading}
              aria-label="Mark N/A"
              data-testid={`button-dismiss-checklist-item-${item.definitionId}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs">Mark N/A</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
